// Inspired by https://github.com/calm/evexia-android/blob/e43d2ab29a4883cb20e751583754c38eed12b449/repository/src/main/java/com/calm/repository/fusion/ProgramRepository.kt#L27

import {
    WorkflowWorkflowInfo,
    WorkflowWorkflowState,
    WorkflowWorkflowType,
} from '@calm/evexia-web-sdk';
import { MINUTE_MS } from '@calm/web-common/dist/time';
import createWorkflowApi from '@rhg/libs/api/create-workflow-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    queryKeys,
    skipToken,
    useQueries,
    useQuery,
    useQueryWithCallback,
} from '@rhg/libs/react-query';
import { useActiveCircle } from '../use-active-circle';
import { useCircle } from '../use-circle';

export const workflowByDefnIdForRecipientQueryOptions = {
    queryKey: ({
        workflowDefnId,
        recipientId,
    }: {
        workflowDefnId?: string;
        recipientId: number;
    }) => {
        return [queryKeys.workflowByDefnId, recipientId, workflowDefnId];
    },
    queryFn: async (
        getAccessTokenSilently: () => Promise<string>,
        circleAuthorizationToken: string,
        circleId: number,
        workflowDefnId?: string
    ) => {
        if (!workflowDefnId) {
            return [];
        }

        const workflowApi = createWorkflowApi(getAccessTokenSilently);

        const response = await workflowApi.v1GetWorkflowsForRecipient({
            circleAuthorizationToken,
            recipientId: circleId,
            workflowDefnId,
            offset: 0,
            limit: 1,
        });

        return response.data.workflows ?? [];
    },
    queryOptions: (
        isAuthenticated: boolean,
        circleId: number,
        circleAuthorizationToken: string | undefined
    ) => {
        return {
            enabled: !!(
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken
            ),
            staleTime: 5 * MINUTE_MS,
        };
    },
};

export function useWorkflowByDefnIdForRecipient({
    circleId,
    workflowDefnId,
}: {
    circleId: number;
    workflowDefnId: string;
}) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT;
    const { queryKey, queryFn, queryOptions } =
        workflowByDefnIdForRecipientQueryOptions;

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({ workflowDefnId, recipientId: circleId }),
        queryFn: circleAuthorizationToken
            ? async () =>
                  queryFn(
                      getAccessTokenSilently,
                      circleAuthorizationToken,
                      circleId,
                      workflowDefnId
                  )
            : skipToken,
        ...queryOptions(isAuthenticated, circleId, circleAuthorizationToken),
    });
    return {
        workflows: data,
        workflow: data?.[0],
        ...otherProps,
    };
}

export function useWorkflowsForActiveCircle(workflowIds: string[]) {
    const { activeCircleId, activeCircle } = useActiveCircle();
    const circleAuthorizationToken = activeCircle?.authorizationJWT;
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn, queryOptions } =
        workflowByDefnIdForRecipientQueryOptions;

    return useQueries({
        queries: workflowIds.map((workflowId) => ({
            queryKey: queryKey({
                workflowDefnId: workflowId,
                recipientId: activeCircleId,
            }),
            queryFn: async () =>
                queryFn(
                    getAccessTokenSilently,
                    circleAuthorizationToken!,
                    activeCircleId,
                    workflowId
                ),
            ...queryOptions(
                isAuthenticated,
                activeCircleId,
                circleAuthorizationToken
            ),
        })),
    });
}

export const workflowByTypeForRecipientQueryOptions = {
    queryKey: ({
        workflowType,
        recipientId,
        workflowStates,
    }: {
        workflowType?: WorkflowWorkflowType;
        recipientId: number;
        workflowStates?: WorkflowWorkflowState[];
    }) => {
        return [
            queryKeys.workflowByType,
            recipientId,
            workflowType,
            workflowStates,
        ];
    },
    queryFn: async (
        getAccessTokenSilently: () => Promise<string>,
        circleAuthorizationToken: string,
        circleId: number,
        workflowType?: WorkflowWorkflowType,
        workflowStates?: WorkflowWorkflowState[]
    ) => {
        const workflowApi = createWorkflowApi(getAccessTokenSilently);

        const response = await workflowApi.v1GetWorkflowsForRecipient({
            circleAuthorizationToken,
            recipientId: circleId,
            workflowType,
            workflowStates,
        });

        return response.data.workflows ?? [];
    },
    queryOptions: (
        isAuthenticated: boolean,
        circleId: number,
        circleAuthorizationToken: string,
        workflowType?: WorkflowWorkflowType
    ) => {
        return {
            onSuccess: (data: WorkflowWorkflowInfo[]) => {
                data.forEach((workflow) => {
                    const individualQk =
                        workflowByDefnIdForRecipientQueryOptions.queryKey({
                            workflowDefnId: workflow.workflowDefn.defnId,
                            recipientId: circleId,
                        });
                    queryClient.setQueryData(individualQk, [workflow]);
                });
            },
            enabled: !!(
                isAuthenticated &&
                circleId > 0 &&
                workflowType &&
                circleAuthorizationToken
            ),
            staleTime: 5 * MINUTE_MS,
        };
    },
};

export function useWorkflowsForRecipientByType({
    circleId,
    workflowType,
    workflowStates,
}: {
    circleId: number;
    workflowType?: WorkflowWorkflowType;
    workflowStates?: WorkflowWorkflowState[];
}) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } =
        workflowByTypeForRecipientQueryOptions;

    const qK = queryKey({
        workflowType,
        recipientId: circleId,
        workflowStates,
    });

    const { data, ...otherProps } = useQueryWithCallback({
        queryKey: qK,
        queryFn: async () =>
            queryFn(
                getAccessTokenSilently,
                circleAuthorizationToken,
                circleId,
                workflowType,
                workflowStates
            ),
        ...queryOptions(
            isAuthenticated,
            circleId,
            circleAuthorizationToken,
            workflowType
        ),
        enabled: !!(
            isAuthenticated &&
            circleId > 0 &&
            workflowType &&
            circleAuthorizationToken
        ),
    });
    return {
        workflows: data,
        ...otherProps,
    };
}

export function useActiveCircleWorkflowsByType(
    workflowType: WorkflowWorkflowType,
    workflowStates?: WorkflowWorkflowState[]
) {
    const { activeCircleId } = useActiveCircle();

    return useWorkflowsForRecipientByType({
        circleId: activeCircleId,
        workflowType,
        workflowStates,
    });
}

export function useAllWorkflowInstances({
    workflowStates,
}: {
    workflowStates?: WorkflowWorkflowState[];
}) {
    const { activeCircleId, activeCircle } = useActiveCircle();
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const workflowApi = createWorkflowApi(getAccessTokenSilently);

    const circleAuthorizationToken = activeCircle?.authorizationJWT ?? '';
    const queryFn = async () => {
        const response = await workflowApi.getAllWorkflowInstances({
            circleAuthorizationToken,
            recipientId: activeCircleId,
            workflowStates,
        });
        return response.data.items;
    };
    const enabled = !!(
        isAuthenticated &&
        activeCircleId > 0 &&
        circleAuthorizationToken
    );
    const queryKey = [
        queryKeys.allWorkflowInstances,
        activeCircleId,
        workflowStates,
    ];

    const result = useQuery({
        queryKey,
        queryFn,
        enabled,
    });
    return { workflows: result.data, ...result };
}
